import { SearchType } from '@brightdrop/bd-ui';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link, type LinkProps } from 'react-router-dom';
import { useGetProfileByIdQuery } from '~/api/generated-api';
import { ACCOUNT_USER_LIST_PATH } from '~/common/models/pages/accountPages.model';

interface UserListLinkProps extends Omit<LinkProps, 'to'> {
  href: string;
}

interface UserAlertLabelProps {
  userId: string;
  searchKey: string;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  /** Called when the user-list link inside the notification is clicked. */
  onClick?: () => void;
}

const UserAlertLabel = (props: UserAlertLabelProps): JSX.Element => {
  const { userFirstName, userLastName, userEmail, userId, searchKey, onClick } =
    props;

  const { data } = useGetProfileByIdQuery(
    {
      id: userId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: Boolean(userFirstName && userLastName && userEmail),
    }
  );

  const usersURL: string = useMemo(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('searchTerm', data?.result?.email ?? userEmail ?? '');
    searchParams.set('searchType', SearchType.USER_EMAIL);
    return `${ACCOUNT_USER_LIST_PATH}?${searchParams.toString()}`;
  }, [data?.result?.email, userEmail]);

  const displayFirstName: string = useMemo(
    () => userFirstName ?? data?.result?.firstName ?? '',
    [userFirstName, data?.result?.firstName]
  );
  const displayLastName: string = useMemo(
    () => userLastName ?? data?.result?.lastName ?? '',
    [userLastName, data?.result?.lastName]
  );
  const nameFallback: string = useMemo(
    () => (displayFirstName || displayLastName ? '' : 'New user'),
    [displayFirstName, displayLastName]
  );

  const UserListLink = (props: UserListLinkProps): JSX.Element => (
    <Link
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      to={props.href}
      data-testid="alerts--user-list-link"
    />
  );

  return (
    <span data-testid="alerts--user-alert-label">
      <Trans
        i18nKey={`settings:notifications.messages.${searchKey.toLowerCase()}`}
        components={{
          alink: <UserListLink href={usersURL} />,
        }}
        values={{
          alert_object_user_first_name: displayFirstName || nameFallback,
          alert_object_user_last_name: displayLastName || '',
        }}
      />
    </span>
  );
};

export default UserAlertLabel;
